import React, { useEffect, useState } from 'react'
import { client } from '@Utils'
import { Pagination } from '@CommonComponents/buttons'
import { usePageRequest } from '@CommonComponents'
import { GradeLogViewer } from './GradeLogViewer'

const styles = {
  container: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e9ecef',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '2px solid #004876',
  },
  title: {
    margin: 0,
    color: '#004876',
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  badge: {
    backgroundColor: '#dc3545',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: 400,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#6c757d',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6c757d',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '12px',
    color: '#28a745',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
  },
}

export const GradeLogListView = ({ queryParams }) => {
  const queryString = new URLSearchParams(queryParams).toString()
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [gradeLogs, setGradeLogs] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const {
    page: currentPage,
    attempt: pageAttempt,
    request: changePage,
    settle: settlePage,
  } = usePageRequest()

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1)
  }

  useEffect(() => {
    // A later page or refresh replaces this load, so its response is dropped.
    let cancelled = false
    const fetchGradeLogs = async () => {
      setIsLoading(true)
      try {
        const resp = await client.get(
          `/account/admin_lti13.getGradeLogs/-/${currentPage}/?${queryString}`
        )
        if (cancelled) return
        setGradeLogs(resp.data.gradeLogs)
        setTotalPages(resp.data.totalPages)
        setPageSize(resp.data.pageSize)
        settlePage()
      } catch (err) {
        if (cancelled) return
        settlePage(err)
        console.error(err)
        if (err.response?.data?.error) alert(err.response.data.error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchGradeLogs()
    return () => {
      cancelled = true
    }
  }, [pageAttempt, refreshTrigger])

  const unresolvedCount = gradeLogs.filter((log) => !log.resolved).length

  // Stays mounted below the loading message so the pager can report its
  // request.
  const pagination =
    totalPages > 1 ? (
      <Pagination
        currentPage={currentPage}
        rate={pageSize}
        total={totalPages * pageSize}
        setCurrentPage={changePage}
      />
    ) : null

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>
          <i className="fa-regular fa-exclamation-triangle mr-2" />
          Failed Grade Push Attempts
        </h3>
        {!isLoading && gradeLogs.length > 0 && (
          <span style={styles.badge}>
            {unresolvedCount} unresolved
          </span>
        )}
      </div>

      {isLoading ? (
        <div style={styles.loadingContainer}>
          <i className="fa-regular fa-spinner fa-spin fa-2x mr-3" />
          <span>Loading grade logs...</span>
        </div>
      ) : gradeLogs.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <i className="fa-regular fa-check-circle" />
          </div>
          <p style={{ margin: 0, fontWeight: 400 }}>No failed grade pushes</p>
          <p style={{ margin: '8px 0 0', fontSize: '0.9rem' }}>
            All grades have been successfully synced with the LMS.
          </p>
        </div>
      ) : (
        <div>
          {gradeLogs.map((gradeLog, i) => (
            <GradeLogViewer
              key={gradeLog.id}
              index={i + 1 + (currentPage - 1) * pageSize}
              gradeLog={gradeLog}
              onRefresh={handleRefresh}
            />
          ))}
        </div>
      )}
      {pagination}
    </div>
  )
}
